import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { Card } from './Card';
import { ErrorNote, SuccessNote } from './Feedback';
import { ReasonField, reasonError } from './Form';
import { ConfirmDialog } from './Overlay';
import { toast } from './Toast';

export type Json = Record<string, unknown>;

/** What a page knows about the text being edited before the server sees it. */
export interface JsonCheck {
  error: string | null;
  warnings: string[];
  parsed: Json | null;
}

/** Parses the editor's text as one JSON object (the page adds its own warnings). */
export function parseJsonObject(text: string): JsonCheck {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return { error: `JSON 格式錯誤：${e instanceof Error ? e.message : String(e)}`, warnings: [], parsed: null };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { error: '設定必須是一個 JSON object。', warnings: [], parsed: null };
  return { error: null, warnings: [], parsed: parsed as Json };
}

/** Warnings for top-level keys the server's schema doesn't know (it refuses them on save). */
export const unknownKeys = (c: Json, known: readonly string[]) =>
  Object.keys(c)
    .filter((k) => !known.includes(k))
    .map((k) => `未知的欄位「${k}」：伺服器會拒絕。`);

type DiffLine = { kind: 'same' | 'add' | 'del'; text: string };

/** Line diff (LCS); configs are a few hundred lines, so the quadratic table is fine. */
function diffLines(a: string[], b: string[]): DiffLine[] {
  const n = a.length;
  const m = b.length;
  const t: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) t[i]![j] = a[i] === b[j] ? t[i + 1]![j + 1]! + 1 : Math.max(t[i + 1]![j]!, t[i]![j + 1]!);
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: 'same', text: a[i]! });
      i++;
      j++;
    } else if (t[i + 1]![j]! >= t[i]![j + 1]!) out.push({ kind: 'del', text: a[i++]! });
    else out.push({ kind: 'add', text: b[j++]! });
  }
  while (i < n) out.push({ kind: 'del', text: a[i++]! });
  while (j < m) out.push({ kind: 'add', text: b[j++]! });
  return out;
}

/** Only the changed lines and two lines of context around them. */
function compact(lines: DiffLine[]): Array<DiffLine | { kind: 'gap' }> {
  const keep = lines.map((l, i) => l.kind !== 'same' || lines.slice(Math.max(0, i - 2), i + 3).some((x) => x.kind !== 'same'));
  const out: Array<DiffLine | { kind: 'gap' }> = [];
  lines.forEach((l, i) => {
    if (keep[i]) out.push(l);
    else if (out[out.length - 1]?.kind !== 'gap') out.push({ kind: 'gap' });
  });
  return out;
}

const pretty = (v: unknown) => JSON.stringify(v, null, 2);

/** What the confirm step adds for a risky change: a warning, and whether it may go ahead yet. */
export interface ConfirmExtra {
  node: ReactNode;
  canConfirm: boolean;
  danger: boolean;
}

/**
 * A stored JSON document edited as text: checked as it's typed, reviewed as a diff with a reason,
 * then saved whole (the server validates it and refuses a bad one). The page supplies its checks,
 * the side panel and any extra confirmation a risky change needs.
 */
export function JsonDocEditor(props: {
  /** The stored document (its `revision` is shown, and bumped by the server on save). */
  doc: Json & { revision: number };
  label: string;
  check: (text: string) => JsonCheck;
  canSave: boolean;
  cantSaveWhy?: string;
  save: (doc: Json, note: string) => Promise<{ revision: number }>;
  saving: boolean;
  saveError: unknown;
  /** Shown once a save went through. */
  savedNote: (revision: number) => ReactNode;
  aside?: (c: JsonCheck) => ReactNode;
  confirmExtra?: (parsed: Json, saved: Json) => ConfirmExtra | null;
  rows?: number;
}) {
  const { doc, check, confirmExtra } = props;
  const [text, setText] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [note, setNote] = useState('');
  const [saved, setSaved] = useState<number | null>(null);

  useEffect(() => setText(pretty(doc)), [doc]);

  const saved0 = useMemo(() => pretty(doc), [doc]);
  const c = useMemo(() => check(text), [check, text]);
  const diff = useMemo(() => (c.parsed ? diffLines(saved0.split('\n'), pretty(c.parsed).split('\n')) : []), [c.parsed, saved0]);
  const changed = diff.some((l) => l.kind !== 'same');
  const extra = c.parsed ? (confirmExtra?.(c.parsed, doc) ?? null) : null;

  const submit = () =>
    void props.save(c.parsed!, note.trim()).then((after) => {
      setReviewing(false);
      setNote('');
      setSaved(after.revision);
      toast(`已儲存 revision ${after.revision}`);
    }, () => undefined);

  return (
    <div className="split">
      <Card title="設定 JSON" sub={`現時 revision ${doc.revision}`}>
        <div className="stack">
          <textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setSaved(null);
            }}
            spellCheck={false}
            rows={props.rows ?? 34}
            className="mono"
            style={{ width: '100%', fontSize: 12.5, lineHeight: 1.5 }}
            aria-label={props.label}
          />
          {c.error && (
            <div className="warnBox" role="alert">
              <strong>{c.error}</strong>
            </div>
          )}
          {saved !== null && <SuccessNote>{props.savedNote(saved)}</SuccessNote>}
          <div className="row">
            <Button variant="primary" disabled={!!c.error || !changed || !props.canSave} title={props.canSave ? undefined : props.cantSaveWhy} onClick={() => setReviewing(true)}>
              檢查變更並儲存…
            </Button>
            <Button disabled={!changed} onClick={() => setText(saved0)}>
              還原
            </Button>
            <span className="small faint">{changed ? `${diff.filter((l) => l.kind !== 'same').length} 行有改動` : '沒有改動'}</span>
          </div>
        </div>
      </Card>
      <aside className="stack">
        {props.aside?.(c)}
        {c.warnings.length > 0 && (
          <Card title="提示">
            <ul style={{ margin: 0, paddingLeft: 18 }} className="small">
              {c.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </Card>
        )}
      </aside>
      <ConfirmDialog
        open={reviewing}
        title="確認變更"
        tone={extra?.danger ? 'danger' : 'primary'}
        confirmLabel="儲存並發佈"
        busy={props.saving}
        canConfirm={!reasonError(note) && (extra?.canConfirm ?? true)}
        onCancel={() => setReviewing(false)}
        onConfirm={submit}
        error={props.saveError ? <ErrorNote error={props.saveError} /> : null}
      >
        <pre className="diff" aria-label="變更">
          {compact(diff).map((l, i) =>
            l.kind === 'gap' ? (
              <span key={i} className="faint">
                {'  ⋯\n'}
              </span>
            ) : (
              <span key={i} className={l.kind === 'add' ? 'add' : l.kind === 'del' ? 'del' : undefined}>
                {`${l.kind === 'add' ? '+ ' : l.kind === 'del' ? '- ' : '  '}${l.text}\n`}
              </span>
            ),
          )}
        </pre>
        {extra?.node}
        <ReasonField value={note} onChange={setNote} label="改了甚麼" autoFocus />
      </ConfirmDialog>
    </div>
  );
}
